// V9.15 · Alerta de Cadastro Incompleto — alunos que faltam CPF, e-mail, endereço,
// data de nascimento ou responsável vinculado. Só leitura + atalho para o Aluno 360.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Loader2, Search, Download, ArrowRight, RefreshCw, Send } from 'lucide-react';
import api from '@/lib/api';

const FIELDS = [
  ['', 'Todos os pendentes'],
  ['cpf', 'CPF'],
  ['email', 'E-mail'],
  ['address', 'Endereço'],
  ['birth_date', 'Data de nascimento'],
  ['guardian', 'Responsável vinculado'],
];

const TONE = {
  cpf: 'bg-rose-50 text-rose-700 border-rose-200',
  email: 'bg-amber-50 text-amber-800 border-amber-200',
  address: 'bg-sky-50 text-sky-700 border-sky-200',
  birth_date: 'bg-violet-50 text-violet-700 border-violet-200',
  guardian: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export default function CadastrosIncompletos() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [linkPedido, setLinkPedido] = useState(null);

  // V9.17 · gera o link público para a família completar os dados que faltam
  const pedirFamilia = async (row) => {
    const groups = [];
    if (row.missing.includes('cpf')) groups.push('cpf');
    if (row.missing.includes('address')) groups.push('address');
    if (row.missing.includes('email')) groups.push('contact');
    if (row.missing.includes('birth_date')) groups.push('birth');
    try {
      const { data } = await api.post('/family-requests', {
        student_id: row.id, groups: groups.length ? groups : ['cpf', 'address'],
      });
      const url = `${window.location.origin}${data.path}`;
      setLinkPedido({ ...data, url, student: row.name });
      try { await navigator.clipboard.writeText(url); toast.success('Link copiado! Envie para a família.'); }
      catch (_) { toast.success('Link gerado.'); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível gerar o link.');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/students-completeness', {
        params: { missing: missing || undefined, q: q || undefined, page, page_size: 50 },
      });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao carregar pendências de cadastro');
    } finally { setLoading(false); }
  }, [missing, q, page]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const rows = data?.items || [];
    if (!rows.length) return toast.error('Nada para exportar nesta página.');
    const csv = ['Aluno;Matrícula;Turma;Pendências',
      ...rows.map(r => `"${r.name || ''}";"${r.registration_number || ''}";"${r.class_name || ''}";"${(r.missing_labels || []).join(' · ')}"`)]
      .join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8' }));
    a.download = `cadastros-incompletos-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const s = data?.summary;
  const labels = s?.field_labels || {};

  return (
    <div className="p-6 space-y-5" data-testid="cadastros-incompletos-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-amber-500" /> Cadastros incompletos
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Alunos ativos que ainda faltam CPF, e-mail, endereço, nascimento ou responsável vinculado
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="btn-reload-incompletos">
            <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="btn-export-incompletos">
            <Download className="w-4 h-4 mr-1" /> Exportar CSV
          </Button>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 xl:pr-16" data-testid="incompletos-resumo">
          <Card className="border-amber-200 bg-amber-50/60">
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-amber-700">{s.incomplete_total}</div>
              <div className="text-[11px] uppercase tracking-wide text-amber-800">Pendentes</div>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50/60">
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-5 h-5" />{s.complete_total}
              </div>
              <div className="text-[11px] uppercase tracking-wide text-emerald-800">Completos</div>
            </CardContent>
          </Card>
          {Object.entries(s.by_field).map(([k, v]) => (
            <Card key={k} className="border-border/60">
              <CardContent className="p-3">
                <div className="text-2xl font-bold">{v}</div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">sem {labels[k] || k}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {linkPedido && (
        <Card className="border-violet-300 bg-violet-50/60" data-testid="link-pedido-familia">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-violet-900">
                Link para a família de {linkPedido.student}
              </p>
              <p className="text-xs text-violet-700 break-all">{linkPedido.url}</p>
              <p className="text-[11px] text-violet-600 mt-0.5">
                Pede: {(linkPedido.group_labels || []).join(' · ')} · válido até{' '}
                {new Date(linkPedido.expires_at).toLocaleDateString('pt-BR')}
              </p>
            </div>
            <a href={`https://wa.me/?text=${encodeURIComponent(`Olá! Para concluir o cadastro de ${linkPedido.student}, complete os dados neste link seguro: ${linkPedido.url}`)}`}
              target="_blank" rel="noreferrer" data-testid="btn-whatsapp-pedido">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">Enviar no WhatsApp</Button>
            </a>
            <Button size="sm" variant="outline" onClick={() => setLinkPedido(null)}>Fechar</Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-border/60">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Buscar aluno pelo nome…" className="pl-10" value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="input-busca-incompletos" />
          </div>
          <div className="flex flex-wrap gap-1">
            {FIELDS.map(([v, label]) => (
              <button key={v || 'all'} type="button"
                onClick={() => { setMissing(v); setPage(1); }}
                className={`text-xs font-semibold rounded-full border px-3 py-1.5 transition-colors ${
                  missing === v ? 'bg-slate-900 text-white border-slate-900' : 'bg-white hover:bg-slate-50'}`}
                data-testid={`filtro-${v || 'todos'}`}>
                {label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-14"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (data?.items || []).length === 0 ? (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="py-12 text-center" data-testid="incompletos-vazio">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600 mb-3" />
            <p className="font-semibold text-emerald-800">Nenhuma pendência neste filtro</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border/60 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tabela-incompletos">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left p-3">Aluno</th>
                  <th className="text-left p-3">Matrícula</th>
                  <th className="text-left p-3">Turma</th>
                  <th className="text-left p-3">O que falta</th>
                  <th className="p-3" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-slate-50/70" data-testid={`row-incompleto-${r.id}`}>
                    <td className="p-3 font-semibold">{r.name}</td>
                    <td className="p-3 text-muted-foreground">{r.registration_number || '—'}</td>
                    <td className="p-3 text-muted-foreground">{r.class_name || '—'}</td>
                    <td className="p-3">
                      {r.missing.map((m, i) => (
                        <Badge key={m} variant="outline" className={`mr-1 mb-1 ${TONE[m] || ''}`}>
                          {r.missing_labels[i]}
                        </Badge>
                      ))}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="mr-1 border-violet-300 text-violet-700"
                        onClick={() => pedirFamilia(r)} data-testid={`btn-pedir-familia-${r.id}`}>
                        <Send className="w-3 h-3 mr-1" /> Pedir à família
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/v2/aluno/${r.id}`)}
                        data-testid={`btn-resolver-${r.id}`}>
                        Resolver <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between p-3 border-t text-xs text-muted-foreground">
            <span data-testid="incompletos-total">
              {data.filtered_total} aluno(s) neste filtro · página {data.page}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)} data-testid="btn-prev-page">Anterior</Button>
              <Button size="sm" variant="outline"
                disabled={page * data.page_size >= data.filtered_total}
                onClick={() => setPage((p) => p + 1)} data-testid="btn-next-page">Próxima</Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
